/**
 * Kaaba drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_may_june`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const kaabaDrawingsPack: ToilePack = {
    slug: 'kaaba-drawings',
    category: 'season',
    name: 'Kaaba drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'simple', label: 'Simple Kaaba', subject: 'the Kaaba as a plain cube seen from a corner, the gold band two-thirds up its walls and the gold door raised on one face'},
        {key: 'haram', label: 'In the Haram', subject: 'the Kaaba at the center of the white marble courtyard of the Masjid al-Haram, the arcades and minarets around it and the clock tower rising behind'},
        {key: 'door', label: 'Kaaba door', subject: "a close view of the Kaaba's gold door set in the black kiswa, the embroidered band above it and the curtain of the door hanging in its folds"},
    ],
    styles: [
        {key: 'easy', label: 'Easy to copy', style: 'a few bold black lines and simple shapes, the kiswa filled solid black, the band and door left as open outlines, easy to copy step by step', palette: 'black ink only', framing: 'The drawing centered on the page with a wide white margin'},
        {key: 'coloring', label: 'Coloring page', style: 'clean closed black outlines with every panel, arch and fold left open to color, no shading', palette: 'black ink only', framing: 'The drawing filling the page with a thin white margin'},
        {key: 'pencil', label: 'Pencil sketch', style: 'a graphite pencil sketch with confident outlines and soft hatched shading on the black kiswa and the shadowed walls', palette: 'gray graphite only', framing: 'The drawing centered with a white margin, faint construction lines left visible'},
    ],
    directions: ['at night', 'with pilgrims', 'watercolor'],
    directionHint: 'try: at night',
    seo: {
        title: 'Kaaba Drawing: Simple Cube, In the Haram, Door',
        metaDescription: 'Printable Kaaba drawings to copy or color: a simple cube, the Kaaba in the Haram and its gold door, in three styles.',
        h1: 'Kaaba drawing: simple cube, in the Haram, the gold door',
        tagline: 'Nine pictures of the Kaaba in three styles: easy to copy, a coloring page and a pencil sketch.',
        intro: 'Printable drawings of the Kaaba in Mecca, for a child to copy or color during Hajj and Eid al-Adha, or for a school project. They show the Kaaba as a simple cube within the Masjid al-Haram, and as a close view of its gold door. Easy to copy, coloring page or pencil sketch. No people and no text appear. Type a different view, such as at night, and the same styles carry it.',
        useCases: [
            'a school project on the Hajj',
            'a drawing to copy step by step',
            'a coloring page during Eid al-Adha',
            'a drawing lesson for a child',
        ],
        faq: [
            {q: 'Are these photographs?', a: 'No, they are drawings, not photographs.'},
            {q: 'What is the easy to copy style?', a: 'A few bold black lines, with the kiswa filled solid black and the band and door left as open outlines.'},
            {q: 'How is the Kaaba shown?', a: 'With its black kiswa, the gold band of calligraphy and the gold door.'},
        ],
    },
}
